import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Loader2, Download, Copy, Trash2, Trophy, Mail } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const TrailerItem = ({ tr, onDelete, t, focus }) => {
  const copy = async () => {
    try { await navigator.clipboard.writeText(`${tr.title}\n${tr.description}\n${tr.hashtags.join(" ")}`); toast.success(t("tr.caption_copied")); } catch { toast.error(t("tr.copy_failed")); }
  };
  return (
    <div className={`rounded-2xl bg-black/30 border p-4 flex flex-col sm:flex-row gap-4 ${focus ? "border-[#3ad7ff] shadow-[0_0_24px_rgba(58,215,255,.35)]" : "border-white/10"}`} data-testid={`trailer-item-${tr.id}`}>
      <div className={`shrink-0 rounded-xl overflow-hidden bg-black grid place-items-center ${tr.aspect === "9:16" ? "w-full sm:w-44 aspect-[9/16]" : "w-full sm:w-72 aspect-video"}`}>
        {tr.status === "done" ? <video src={mediaUrl(`/trailers/${tr.id}/file`)} controls preload="metadata" playsInline className="w-full h-full object-contain" data-testid="trailer-video" />
          : tr.status === "failed" ? <span className="text-xs text-[#ff5a8a] px-3 text-center" data-testid="trailer-failed">{t("tr.failed")}</span>
            : <span className="text-xs text-slate-400 flex items-center gap-2" data-testid="trailer-rendering"><Loader2 className="w-4 h-4 animate-spin text-[#3ad7ff]" />{t("tr.rendering")}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold text-white text-lg flex items-center gap-2" data-testid="trailer-title">{tr.title}{tr.weekly && <span className="rounded-full bg-[#a746ff]/25 px-2 py-0.5 text-[9px] font-semibold text-[#d9a8ff]" data-testid="trailer-weekly-badge">{t("tr.weekly_badge")}</span>}{tr.live_sid && <span className="rounded-full bg-[#ff3b6b]/25 px-2 py-0.5 text-[9px] font-semibold text-[#ff9db4]" data-testid="trailer-live-badge">{t("tr.live_badge")}</span>}</div>
        <div className="text-xs text-[#ffc94d] mt-0.5">{tr.hook}</div>
        <ol className="mt-3 space-y-1">
          {tr.items.map((it, i) => (
            <li key={it.clip_id} className="text-xs text-slate-300 flex gap-2"><span className="font-mono text-[#3ad7ff]">#{tr.items.length - i}</span><span className="truncate">{it.label || it.title}</span></li>
          ))}
        </ol>
        <p className="text-xs text-slate-500 mt-3 line-clamp-2">{tr.description} {tr.hashtags.join(" ")}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {tr.status === "done" && <a href={mediaUrl(`/trailers/${tr.id}/file`, "&download=1")} data-testid="trailer-download-btn" className="cx-btn rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5"><Download className="w-3.5 h-3.5" />{t("tr.download")}</a>}
          <button onClick={copy} data-testid="trailer-copy-btn" className="cx-metal rounded-full px-4 py-2 text-xs flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" />{t("tr.copy_caption")}</button>
          <button onClick={() => onDelete(tr)} data-testid="trailer-delete-btn" className="cx-metal rounded-full px-4 py-2 text-xs flex items-center gap-1.5 text-slate-400"><Trash2 className="w-3.5 h-3.5" />{t("tr.delete")}</button>
        </div>
      </div>
    </div>
  );
};

const WeeklySwitch = ({ t, demo }) => {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/trailers/weekly").then((r) => setOn(r.data.on)).catch(() => setOn(false)); }, []);
  const toggle = async (v) => {
    setOn(v);
    try { await api.put("/trailers/weekly", { on: v }); toast.success(t(v ? "tr.weekly_on" : "tr.weekly_off")); } catch (e) { setOn(!v); toast.error(formatApiError(e)); }
  };
  return (
    <label className="mt-4 relative flex items-center gap-3 rounded-xl border border-[#a746ff]/30 bg-[#a746ff]/10 px-4 py-3 cursor-pointer" data-testid="trailer-weekly">
      <Mail className="w-4 h-4 text-[#d9a8ff] shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block text-sm text-white font-semibold">{t("tr.weekly_title")}</span>
        <span className="block text-[11px] text-slate-400">{t("tr.weekly_desc")}</span>
      </span>
      <Switch checked={!!on} disabled={demo || on === null} onCheckedChange={toggle} data-testid="trailer-weekly-switch" />
    </label>
  );
};

export const TrailerStudio = ({ demo }) => {
  const { t } = useI18n();
  const [days, setDays] = useState(7);
  const [cand, setCand] = useState(null);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const focus = new URLSearchParams(window.location.search).get("trailer");
  const load = useCallback(() => api.get("/trailers").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => {
    if (!focus || !list.length) return;
    document.querySelector(`[data-testid="trailer-item-${focus}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focus, list.length]);
  useEffect(() => { api.get(`/trailers/candidates?days=${days}`).then((r) => setCand(r.data)).catch(() => setCand(null)); }, [days]);
  useEffect(() => { load(); }, [load]);
  const rendering = list.some((x) => x.status === "rendering");
  useEffect(() => { if (!rendering) return undefined; const i = setInterval(load, 4000); return () => clearInterval(i); }, [rendering, load]);
  const create = async () => {
    setBusy(true);
    try { await api.post("/trailers", { days }); toast.success(t("tr.started")); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const del = async (tr) => {
    if (!window.confirm(t("tr.confirm_delete"))) return;
    try { await api.delete(`/trailers/${tr.id}`); } catch (e) { toast.error(formatApiError(e)); }
    load();
  };
  const enough = (cand?.count || 0) >= 2;
  return (
    <section className="cx-card rounded-2xl p-5 sm:p-6 mt-8 relative overflow-hidden" data-testid="trailer-studio">
      <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[#ff2bd6]/10 blur-3xl pointer-events-none" />
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 relative">
        <div>
          <div className="flex items-center gap-2 text-white font-semibold"><Trophy className="w-5 h-5 text-[#ffc94d]" />{t("tr.title")}</div>
          <p className="text-xs text-slate-400 mt-1 max-w-xl">{t("tr.sub")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[7, 14, 30].map((n) => (
            <button key={n} onClick={() => setDays(n)} data-testid={`trailer-days-${n}`} className={`rounded-full px-3 py-1.5 text-[11px] ${days === n ? "cx-metal cx-metal-active" : "cx-metal"}`}>{t("tr.days").replace("{n}", n)}</button>
          ))}
          <button onClick={create} disabled={demo || busy || rendering || !enough} data-testid="trailer-create-btn"
            className="cx-btn rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-2 disabled:opacity-40">
            {busy || rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />}{t("tr.create")}
          </button>
        </div>
      </div>
      <p className="text-[11px] mt-3 relative" data-testid="trailer-candidates">
        {enough ? <span className="text-[#22e3a1]">{t("tr.found").replace("{n}", cand.count)}</span> : <span className="text-slate-500">{t("tr.need")}</span>}
        <span className="text-slate-600"> · {t("tr.limit")}</span>
      </p>
      <WeeklySwitch t={t} demo={demo} />
      {list.length > 0 && <div className="mt-5 space-y-3 relative">{[...list.slice(0, 3), ...list.slice(3).filter((x) => x.id === focus)].map((tr) => <TrailerItem key={tr.id} tr={tr} onDelete={del} t={t} focus={tr.id === focus} />)}</div>}
    </section>
  );
};
